import { useState } from 'react';
import { useApi } from '@bumble/app/hooks/use-api';
import { apiError } from '@bumble/app/utils/api-error';
import { AddressStructured } from '@bumble/app/__generated__/Api';
import { Box, Button, Grid, TextField, Typography } from '@mui/material';
import { useMutation } from '@tanstack/react-query';
import { toast } from 'react-toastify';

interface RouterSiteEditProps {
  onSubmit: () => void;
  cpeId: string;
  cpeName: string;
  siteAddress?: AddressStructured;
  providerServiceID?: string;
  clientCircuitID?: string;
  pingDnsIp?: string;
  pingEmailNotification?: string;
  pingWebhookNotification?: string;
  defaultGatewayIP?: string;
}

export const RouterSiteEdit = ({
  onSubmit, cpeId, cpeName, siteAddress, providerServiceID, clientCircuitID,
  pingDnsIp, pingEmailNotification, pingWebhookNotification, defaultGatewayIP,
}: RouterSiteEditProps) => {
  const api = useApi();
  const [newCpeName, setNewCpeName] = useState<string>(cpeName);
  const [streetAddress, setStreetAddress] = useState(siteAddress?.street_address ?? '');
  const [city, setCity] = useState(siteAddress?.city ?? '');
  const [stateProvince, setStateProvince] = useState(siteAddress?.state_province ?? '');
  const [postalCode, setPostalCode] = useState(siteAddress?.postal_code ?? '');
  const [country, setCountry] = useState(siteAddress?.country ?? '');
  const [newProviderServiceID, setNewProviderServiceID] = useState<string | undefined>(providerServiceID);
  const [newClientCircuitID, setNewClientCircuitID] = useState<string | undefined>(clientCircuitID);
  const [newPingDnsIp, setNewPingDnsIp] = useState<string | undefined>(pingDnsIp);
  const [newPingEmailNotification, setPingEmailNotification] = useState<string | undefined>(pingEmailNotification);
  const [newPingWebhookNotification, setPingWebhookNotification] = useState<string | undefined>(pingWebhookNotification);
  const [newDefaultGatewayIP, setNewDefaultGatewayIP] = useState<string | undefined>(defaultGatewayIP);

  const SiteUpdatePost = useMutation(api.updateCpeInfoApiV1UpdateCpeInfoPost, {
    onSuccess() {
      toast.success('Updated CPE info successfully.');
      onSubmit();
    },
    onError: apiError,
  });

  const handleSubmit = () => {
    SiteUpdatePost.mutate({
      cpe_id: cpeId,
      cpe_name: newCpeName,
      site_address: { street_address: streetAddress, city, state_province: stateProvince, postal_code: postalCode, country },
      provider_service_id: newProviderServiceID,
      client_circuit_id: newClientCircuitID,
      ping_dns_ip: newPingDnsIp,
      ping_email_notification: newPingEmailNotification,
      ping_webhook_notification: newPingWebhookNotification,
      ping_gw_ip: newDefaultGatewayIP,
    });
  };

  return (
    <Box pt={2}>
      <TextField
        sx={{ mb: 4 }}
        fullWidth
        label="Device ID"
        variant="outlined"
        value={cpeId}
        disabled
      />
      <TextField
        sx={{ mb: 4 }}
        fullWidth
        label="Name"
        variant="outlined"
        value={newCpeName}
        onChange={(e) => setNewCpeName(e.target.value)}
        autoFocus
      />

      <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>Site Address</Typography>
      <Grid container spacing={4} sx={{ mb: 4 }}>
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Street Address"
            variant="outlined"
            value={streetAddress}
            onChange={(e) => setStreetAddress(e.target.value)}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="City"
            variant="outlined"
            value={city}
            onChange={(e) => setCity(e.target.value)}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="State / Province"
            variant="outlined"
            value={stateProvince}
            onChange={(e) => setStateProvince(e.target.value)}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="Postal Code"
            variant="outlined"
            value={postalCode}
            onChange={(e) => setPostalCode(e.target.value)}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="Country"
            variant="outlined"
            value={country}
            onChange={(e) => setCountry(e.target.value)}
          />
        </Grid>
      </Grid>

      <TextField
        sx={{ mb: 4 }}
        fullWidth
        label="Provider Service ID"
        variant="outlined"
        value={newProviderServiceID}
        onChange={(e) => setNewProviderServiceID(e.target.value)}
      />
      <TextField
        sx={{ mb: 4 }}
        fullWidth
        label="Client Circuit ID"
        variant="outlined"
        value={newClientCircuitID}
        onChange={(e) => setNewClientCircuitID(e.target.value)}
      />
      <TextField
        sx={{ mb: 4 }}
        fullWidth
        label="Ping DNS IP"
        variant="outlined"
        value={newPingDnsIp}
        onChange={(e) => setNewPingDnsIp(e.target.value)}
      />
      <TextField
        sx={{ mb: 4 }}
        fullWidth
        label="Default Gateway IP"
        variant="outlined"
        value={newDefaultGatewayIP}
        onChange={(e) => setNewDefaultGatewayIP(e.target.value)}
      />
      <TextField
        sx={{ mb: 4 }}
        fullWidth
        label="Email Address for Alert Notification"
        variant="outlined"
        value={newPingEmailNotification}
        onChange={(e) => setPingEmailNotification(e.target.value)}
      />
      <TextField
        sx={{ mb: 4 }}
        fullWidth
        label="Webhook URL for Alert Notification"
        variant="outlined"
        value={newPingWebhookNotification}
        onChange={(e) => setPingWebhookNotification(e.target.value)}
      />
      <Box display="flex" justifyContent="flex-end" sx={{ mt: 5 }}>
        <Button variant="outlined" onClick={handleSubmit} disabled={!newCpeName}>
          Save CPE info
        </Button>
      </Box>
    </Box>
  );
};
